'use client';

/**
 * MIRACLES CORANIQUES — Iʿjāz ʿIlmī.
 * Bannière dorée, atmosphère cosmique, filtres par domaine, cartes-questions
 * verset visible, CONTENU COMPLET dépliable dans la page (exigence III).
 */
import { useMemo, useState } from 'react';
import { useApp } from '@/lib/store';
import { DICTS } from '@/lib/i18n';
import PageBanner from './PageBanner';
import { MIRACLES, MIRACLE_CATS } from '@/data/miracles';
import { cn } from '@/lib/utils';
import { Atom, Globe2, Dna, Leaf, Sigma, Quote, ChevronDown } from 'lucide-react';

const CAT_ICONS: Record<string, React.ReactNode> = {
  cosmos: <Atom className="h-3.5 w-3.5" />,
  terre: <Globe2 className="h-3.5 w-3.5" />,
  humain: <Dna className="h-3.5 w-3.5" />,
  nature: <Leaf className="h-3.5 w-3.5" />,
  nombres: <Sigma className="h-3.5 w-3.5" />,
};

export default function MiraclesSection() {
  const { uiLang } = useApp();
  const t = DICTS[uiLang];
  const bl = uiLang === 'ar' ? 'fr' : uiLang;
  const [cat, setCat] = useState<string>('all');
  const [openId, setOpenId] = useState<string | null>(null);

  const list = useMemo(
    () => (cat === 'all' ? MIRACLES : MIRACLES.filter((m) => m.cat === cat)),
    [cat]
  );

  /* Étoiles décoratives déterministes */
  const stars = useMemo(
    () => Array.from({ length: 30 }, (_, i) => {
      const s = (i * 6089) % 997;
      return { left: s % 100, top: (s * 3) % 100, size: 1 + (s % 2), delay: (s % 30) / 10 };
    }),
    []
  );

  return (
    <section className="cosmic-bg relative min-h-screen py-10" aria-label={t.miracles.title}>
      {/* Bannière dorée */}
      <PageBanner
        src="/images/banners/miracles_banner.png"
        eyebrow="Iʿjāz ʿIlmī — الإعجاز العلمي"
        title={t.miracles.title}
        sub={t.miracles.subtitle}
      />
      <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
        {stars.map((s, i) => (
          <span key={i} className="twinkle absolute rounded-full bg-[#f7e7b0]"
            style={{ left: `${s.left}%`, top: `${s.top}%`, width: s.size, height: s.size, animationDelay: `${s.delay}s` }} />
        ))}
        {/* Grand halo orbital */}
        <span className="spin-slow absolute -right-40 -top-40 h-[480px] w-[480px] rounded-full border border-[rgba(212,175,55,0.12)]" />
        <span className="spin-slow absolute -right-24 -top-24 h-[320px] w-[320px] rounded-full border border-[rgba(212,175,55,0.18)]" style={{ animationDirection: 'reverse' }} />
      </div>

      <div className="relative mx-auto max-w-6xl px-4 pt-10 sm:px-6">

        {/* Filtres */}
        <div className="mb-8 flex flex-wrap justify-center gap-2">
          {MIRACLE_CATS.map((c) => (
            <button
              key={c}
              onClick={() => setCat(c)}
              className={cn(
                'flex items-center gap-1.5 rounded-full border px-4 py-1.5 text-xs font-bold transition-all',
                cat === c
                  ? 'border-[#d4af37] bg-[#d4af37] text-[#032019]'
                  : 'border-[rgba(212,175,55,0.3)] text-[#cfe0cf]/75 hover:border-[rgba(212,175,55,0.6)] hover:text-[#f0d98c]'
              )}
            >
              {c !== 'all' && CAT_ICONS[c]}
              {t.miracles.cats[c]}
              {c !== 'all' && <span className="opacity-70">({MIRACLES.filter((m) => m.cat === c).length})</span>}
            </button>
          ))}
        </div>

        {/* Cartes-questions — contenu complet dépliable dans la page */}
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {list.map((m) => {
            const open = openId === m.id;
            return (
              <article
                key={m.id}
                className="glass-gold glass-gold-hover group flex flex-col overflow-hidden rounded-2xl"
              >
                {/* Bandeau accent */}
                <span className="h-1 w-full" style={{ background: `linear-gradient(90deg, transparent, ${m.accent}, transparent)` }} aria-hidden />
                <button
                  onClick={() => setOpenId(open ? null : m.id)}
                  aria-expanded={open}
                  className="flex flex-1 flex-col p-5 text-left"
                >
                  <span
                    className="mb-3 inline-flex w-fit items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider"
                    style={{ borderColor: `${m.accent}55`, backgroundColor: `${m.accent}14`, color: m.accent }}
                  >
                    {CAT_ICONS[m.cat]}
                    {t.miracles.cats[m.cat]}
                  </span>
                  <h3 className="text-[15px] font-bold leading-snug text-[#f5f0e1] group-hover:text-[#f0d98c]">
                    {m.title[uiLang]}
                  </h3>
                  <p className="font-arabic mt-0.5 text-base text-[#f0d98c]/70" dir="rtl" lang="ar">{m.title.ar}</p>

                  {/* Verset visible */}
                  <figure className="mt-4 rounded-lg border border-[rgba(212,175,55,0.22)] bg-[rgba(2,25,19,0.65)] p-3">
                    <p className="font-arabic text-[15px] leading-loose text-[#f0d98c]" dir="rtl" lang="ar">{m.verse.ar}</p>
                    <figcaption className="mt-1.5 text-[11px] italic leading-snug text-[#cfe0cf]/70">{m.verse[bl]}</figcaption>
                    <p className="mt-1.5 text-[9.5px] font-bold uppercase tracking-widest text-[#d4af37]/70">{m.verse.src}</p>
                  </figure>

                  <span className="mt-4 inline-flex items-center gap-1.5 text-[12px] font-bold text-[#d4af37]">
                    <ChevronDown className={cn('h-3.5 w-3.5 transition-transform', open && 'rotate-180')} aria-hidden />
                    {open ? t.common.close : t.miracles.discover}
                  </span>
                </button>

                {/* ===== CONTENU COMPLET dans la page ===== */}
                {open && (
                  <div className="border-t border-[rgba(212,175,55,0.2)] bg-[rgba(2,25,19,0.5)] px-5 pb-5 pt-4">
                    <div className="space-y-3">
                      {m.body[bl].map((par, i) => (
                        <p key={i} className="text-[13px] leading-relaxed text-[#e5e0d0]/90">{par}</p>
                      ))}
                    </div>
                    <figure className="mt-4 rounded-xl border border-[rgba(212,175,55,0.35)] bg-[rgba(2,25,19,0.75)] p-4 text-center">
                      <Quote className="mx-auto mb-1.5 h-4 w-4 text-[#d4af37]/60" aria-hidden />
                      <p className="font-arabic text-xl leading-loose text-[#f0d98c]" dir="rtl" lang="ar">{m.verse.ar}</p>
                      <figcaption className="mt-1.5 text-[12px] italic text-[#cfe0cf]/85">{m.verse[bl]}</figcaption>
                      <p className="mt-1.5 text-[10px] font-bold uppercase tracking-widest text-[#d4af37]/70">{m.verse.src}</p>
                    </figure>
                  </div>
                )}
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
